/**
 * Update: 15/04/2024 - Mariam Bawa - update import statements as part of data type refactoring 
 * Update: 18/04/2024 - Mariam Bawa - Cast ELS Income attribute response to work with updated LookupsState
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { ProfileOverviewState } from '../../../redux/reducers/getProfile/getProfileOverviewReducer';
import { SaveDataState } from '../../../redux/reducers/getSaveData/saveDataReducer';
import { getProfileAttributes, putProfileAttributes, resetGetProfileAttributes } from '../../../redux/actions/profile/profile';
import { ProfileAttributesState } from '../../../redux/reducers/getProfile/profileAttributes/getAttributesReducer';
import SubsidyData from '../../../types/subsidy/SubsidyData';
import updateProperties from '../../../utils/DataModifier/DataModifier';
import Total from '../../../components/common/Total/Total';
import "../PIE.css";
import { ControlledAccordion, useAccordionProvider } from "@szhsin/react-accordion";
import AccordionItem, { DefaultAccordionProviderOptions } from '../../../components/common/AccordionItem/AccordionItem';
import RevenueTab from '../Revenues/RevenueTab';
import { getLookups } from '../../../redux/actions/lookups/lookups';
import { IncomeAttributeState } from '../../../redux/reducers/getIncomeAttributes/getIncomeAttributesReducer';
import { getIncomeAttributesBySnapshotId, putIncomeAttributesBySnapshotId } from '../../../redux/actions/incomeAttributes/incomeAttributes';
import IncomeAttributeLookupObject from '../../../types/lookups/IncomeAttributeLookupObject';
import { useFormErrorContext } from '../../../utils/context/FormErrorContext';
import { useNavigation } from '../../../utils/context/NavigationContext';
import ErrorMessages from '../../../components/common/ErrorMessages/ErrorMessages';
import { accordionDisplay, getCurrentCampaignYear, getLastYearSubmittedCampaign, shouldDisplayPreviousTwoYearAttributes, shouldDisplayPreviousYearAttributes } from '../../../utils/previousIncomeAttributes';
import { GET_PREVIOUS_PROFILE_ATTRIBUTES, MANAGE_PREVIOUS_INCOME_ATTRIBUTES_BY_SNAPSHOT_ID } from '../../../redux/actionTypes/actionsTypes';
import SubsidyProfileTab from './SubsidyProfileTab';
import PreviousCampaignIncomeAttributes from '../../../components/common/PreviousCampaignIncomeAttributes/PreviousCampaignIncomeAttributes';
import { selectGetIncomeAttributes, selectLookups, selectProfileGetAttributes, selectProfileGetOverview, selectSaveData } from '../../../redux/selectors/exportSelectorPaths';

const initialSubsidyData: SubsidyData = {
  'nursingPersonalCareTotal': null,
  'programSupportServiceTotal': null,
  'rawFoodTotal': null,
  "globalPierDiem": null,
  'cchsaTotal': null,
  'equalizationAdjustmentTotal': null,
  'aggregateSubsidyTotal': null,
  'structuralComplianceFundingTotal': null,
  'municipalTaxAllowanceTotal': null,
  'additionalConstructionFundingTotal': null,
  'subsidyOtherTotal': null,
  'subsidyOtherDescription': null
}
const subsidyKeys = Object.keys(initialSubsidyData)

export default function Subsidy(): JSX.Element {
  const { t } = useTranslation(["ltc_profile_attribute", "pie"])
  const dispatch = useDispatch()

  const profileOverview: ProfileOverviewState = useSelector(selectProfileGetOverview)
  const profileAttributes: ProfileAttributesState = useSelector(selectProfileGetAttributes)
  const incomeAttributesState: IncomeAttributeState = useSelector(selectGetIncomeAttributes);
  const lookupState = useSelector(selectLookups);
  console.log(lookupState.lookups[1]["nonOperatingIncomeRevenues"])
  const saveData: SaveDataState = useSelector(selectSaveData)
  const currentSnapshotId = Number(sessionStorage.getItem("currentSnapshotId") as string)
  const [subsidyData, setSubsidyData] = useState<SubsidyData>(initialSubsidyData)
  const [totalIncome, setTotalIncome] = useState<number | undefined>(undefined)
  const [totalLoss, setTotalLoss] = useState<number | undefined>(undefined)
  const providerValue = useAccordionProvider(DefaultAccordionProviderOptions)
  const lookupType = "ltc-income-attributes";
  const lossTabName = "rentalLoss";
  const { direction } = useNavigation()
  const { errors, clearErrors } = useFormErrorContext()
  const isInternal = (sessionStorage.getItem("isInternalUser") as string) === "true"
  const currentCampaignYear = getCurrentCampaignYear()

  useEffect(() => {
    dispatch(resetGetProfileAttributes())

    if ((!incomeAttributesState.loading && !incomeAttributesState.success)
      || incomeAttributesState.snapshotId !== currentSnapshotId) {
      dispatch(getIncomeAttributesBySnapshotId(currentSnapshotId));
    }

    if (!profileAttributes.loading) {
      dispatch(getProfileAttributes(profileOverview.profile.profile_type_code, currentSnapshotId))
    }

    const campaign = getLastYearSubmittedCampaign(profileOverview.profile)

    if (campaign) {
      dispatch(getIncomeAttributesBySnapshotId(campaign.currentSnapshotId, MANAGE_PREVIOUS_INCOME_ATTRIBUTES_BY_SNAPSHOT_ID))
      dispatch(getProfileAttributes(profileOverview.profile.profile_type_code, campaign.currentSnapshotId, GET_PREVIOUS_PROFILE_ATTRIBUTES))
    }
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    if (!lookupState.success) {
      dispatch(getLookups(lookupType));
    }
  }, [dispatch, lookupState.success]);

  useEffect(() => {
    if (!profileAttributes.loading && profileAttributes.data) {
      const initData = { ...initialSubsidyData }
      updateProperties(initData, profileAttributes.data)
      setSubsidyData(initData)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileAttributes.data, profileAttributes.loading])

  useEffect(() => {
    if (saveData.saving) {
      const fullData = { ...profileAttributes.data, ...subsidyData }
      clearErrors()
      const triggerSpectral = isInternal || direction !== "backward"

      dispatch(putProfileAttributes(profileOverview.profile.profile_type_code, currentSnapshotId, {
        "profile_attributes": fullData,
        "spectral_section": "subsidy",
        "trigger_spectral": triggerSpectral,
        "is_internal": isInternal
      }));
      const incomeAttributes = (incomeAttributesState.data as Record<string, unknown>[]).filter((data) => data.value !== null || data.description !== null || data.percent !== null);

      dispatch(putIncomeAttributesBySnapshotId(currentSnapshotId, {
        "attributes": incomeAttributes,
        "spectral_section": profileOverview.profile.profile_type_code.toLowerCase() + "-revenue",
        "trigger_spectral": triggerSpectral,
        "is_internal": isInternal
      }));
    }
  }, [currentSnapshotId, dispatch, profileAttributes.data, incomeAttributesState.data, profileOverview.profile.profile_type_code, saveData.saving, subsidyData, clearErrors, direction, isInternal])

  const handleChange = (newAmt: number | undefined | null, name: string, _oldAmt: number | undefined | null) => {
    const na = (newAmt === undefined || newAmt === null) ? null : newAmt
    setSubsidyData({ ...subsidyData, [name]: na })
    calculateBpsTotal({ ...subsidyData, [name]: na })
  }

  const handleCommentChange = (value: string | undefined, name: string) => {
    setSubsidyData({ ...subsidyData, [name]: value ? value : null })
  }

  const handleIncomeAttrChange = (newAmt: number | undefined | null, name: string, _oldAmt: number | undefined | null) => {
    const idx = name.lastIndexOf("-");
    if (idx !== -1) {
      const code = name.substring(idx + 1);
      const row = (incomeAttributesState.data as Record<string, unknown>[]).find(row => row.code === code);
      const na = (newAmt === undefined || newAmt === null) ? null : newAmt
      if (row) {
        if (code === "159") {
          row["percent"] = na;
        } else {
          row['value'] = na;
        }
      } else {
        if (code === "159") {
          (incomeAttributesState.data as Record<string, unknown>[]).push({
            snapshotId: currentSnapshotId,
            code: code,
            description: null,
            value: null,
            type: "5",
            percent: na,
            year: null,
          });
        } else {
          (incomeAttributesState.data as Record<string, unknown>[]).push({
            snapshotId: currentSnapshotId,
            code: code,
            description: null,
            value: na,
            type: "1",
            percent: null,
            year: null,
          });
        }
      }

      // Update totals
      const newTotal = calculateTotal(String(currentCampaignYear), false);
      setTotalIncome(newTotal);
    }
  }

  const handleTotalLoss = (newAmt: number | undefined | null, name: string, oldAmt: number | undefined | null) => {
    let calculated = false;
    let value = 0;
    const idx = name.lastIndexOf("-");
    if (idx !== -1) {
      const code = name.substring(idx + 1);
      const incomeAttributesData = findAttributeData(code);
      const na = (newAmt === undefined || newAmt === null) ? null : newAmt;
      if (lookupType === "ltc-income-attributes" && code === "159") {
        if (incomeAttributesData) {
          incomeAttributesData["percent"] = na;
        } else {
          (incomeAttributesState.data as Record<string, unknown>[]).push({
            snapshotId: currentSnapshotId,
            code: code,
            description: null,
            value: null,
            type: "5",
            percent: na,
            year: null,
          });
        }
        return;
      }

      if (incomeAttributesData) {
        incomeAttributesData["value"] = na;
      } else {
        (incomeAttributesState.data as Record<string, unknown>[]).push({
          snapshotId: currentSnapshotId,
          code: code,
          description: null,
          value: na,
          type: "5",
          percent: null,
          year: null,
        });
      }
    }

    if (typeof totalLoss === "number") {
      value = totalLoss;
    }
    if (typeof newAmt === "number") {
      value = value + newAmt;
      calculated = true;
    }
    if (typeof oldAmt === "number") {
      value = value - oldAmt;
      calculated = true;
    }
    if (calculated) {
      setTotalLoss(value);
    }
  }

  const handleIncomeAttrCommentChange = (value: string, name: string) => {
    const idx = name.lastIndexOf("-");
    if (idx !== -1) {
      const code = name.substring(idx + 1);
      const incomeAttributesData = (incomeAttributesState.data as Record<string, unknown>[]).find(row => row.code === code);
      const v = value.trim() === "" ? null : value.trim();
      if (incomeAttributesData) {
        incomeAttributesData["description"] = v;
      } else {
        (incomeAttributesState.data as Record<string, unknown>[]).push({
          snapshotId: currentSnapshotId,
          code: code,
          description: v,
          value: null,
          type: "1",
          percent: null,
          year: null,
        });
      }
    }
  }

  const calculateBpsTotal = (data: SubsidyData) => {
    let total: number | null = null;
    Object.values(data).forEach((value) => {
      if (value && typeof value === "number") {
        total = (total ?? 0) + value
      }
    })
    const row = (incomeAttributesState.data as Record<string, unknown>[]).find(row => row.code === "28");
    if (row) {
      row['value'] = total;
    } else {
      (incomeAttributesState.data as Record<string, unknown>[]).push({
        snapshotId: currentSnapshotId,
        code: "28",
        description: null,
        value: total,
        type: "1",
        percent: null,
        year: null,
      });
    }
  }

  const calculateTotal = (year: string, previousYearTotal: boolean) => {
    let total = 0;
    let filteredAttributes: Record<string, unknown>[] = [];

    let data = null
    if (previousYearTotal) {
      data = findAllPreviousAttributes(year)
    } else {
      data = incomeAttributesState.data
    }

    if (data) {
      filteredAttributes = (data as Record<string, unknown>[]).filter(row => {
        return (
          ((lookupState.lookups[1]["revenue"] as IncomeAttributeLookupObject[] ?? []).some(f => f.incomeAttributeCode === row['code'])) ||
          ((lookupState.lookups[1]["nonOperatingIncomeRevenues"] as IncomeAttributeLookupObject[] ?? []).some(f => f.incomeAttributeCode === row['code']))
        );
      });

      filteredAttributes.forEach(record => {
        if (record['code'] !== "159" && record['code'] !== "89" && record['value']) {
          total += record['value'] as number
        }
      });
    }
    return total;
  }

  const findAttributeData = (attributeCode: string) => {
    let incomeAttributesData: Record<string, unknown> | undefined = undefined;
    if (incomeAttributesState.data) {
      incomeAttributesData = (incomeAttributesState.data as Record<string, unknown>[]).find(({ code }) => code === attributeCode);
    }
    return incomeAttributesData;
  }

  const findAllPreviousAttributes = (year: string) => {
    const sessionStoragePreviousSnapshotData = sessionStorage.getItem(isInternal ? "previousSnapshotDataInternal" + year : "previousSnapshotData")
    if (sessionStoragePreviousSnapshotData !== null && sessionStoragePreviousSnapshotData !== undefined) {
      let previousSnapshotData = { incomeAttribute: [] }
      previousSnapshotData = JSON.parse(sessionStoragePreviousSnapshotData)
      if (previousSnapshotData.incomeAttribute) {
        return previousSnapshotData.incomeAttribute;
      }
    }
    return null;
  }

  const findPreviousAttributeData = (attributeCode: string, year: string) => {
    let incomeAttributesData: Record<string, unknown> | undefined = undefined;
    const incomeAttributes = findAllPreviousAttributes(year)
    if (incomeAttributes) {
      incomeAttributesData = (incomeAttributes as Record<string, unknown>[]).find(({ code }) => code === attributeCode);
    }
    return incomeAttributesData;
  }

  const calculateTotalLoss = (year: string, previousYearTotal: boolean) => {
    let total = 0;
    let calculated = false;
    const revenueLookups = lookupState.lookups[1] as Record<string, IncomeAttributeLookupObject[]>;
    const lookupAttributes = revenueLookups[lossTabName];
    if (lookupAttributes) {
      lookupAttributes.forEach((attribute) => {
        if (attribute.activeFlag === true) {
          const code = attribute.incomeAttributeCode;
          if (lookupType === "ltc-income-attributes" && code === "159") {
            return;
          }
          let incomeAttributesData = null
          if (previousYearTotal) {
            incomeAttributesData = findPreviousAttributeData(code, year);
          } else {
            incomeAttributesData = findAttributeData(code);
          }
          if (incomeAttributesData && incomeAttributesData.value && incomeAttributesData.value !== 0) {
            total = total + (incomeAttributesData.value as number);
            calculated = true;
          }
        }
      });
    }
    if (calculated) {
      if (previousYearTotal) {
        return total === 0 ? undefined : total
      } else {
        setTotalLoss(total);
      }
    }
    return total === 0 ? undefined : total;
  }

  const getTotalSubsidy = (year: string, previousYearSubsidy: boolean) => {
    let row = null
    if (previousYearSubsidy) {
      row = findPreviousAttributeData("28", year);
    } else {
      row = (incomeAttributesState.data as Record<string, unknown>[]).find(row => row.code === "28")
    }
    return row ? (row["value"] ? row["value"] as number : undefined) : undefined;
  }

  const handleCurrencyChange = (value: number | null, name: keyof SubsidyData) => {
    setSubsidyData({ ...subsidyData, [name]: value })
    calculateBpsTotal({ ...subsidyData, [name]: value })
  };

  useEffect(() => {
    if (!incomeAttributesState.loading && incomeAttributesState.data && lookupState.success && !incomeAttributesState.loading && incomeAttributesState.data) {
      const total = calculateTotal(String(currentCampaignYear), false);
      setTotalIncome(total);
      if (totalLoss === undefined) {
        calculateTotalLoss(String(currentCampaignYear), false);
      }
    }
    // eslint-disable-next-line
  }, [incomeAttributesState, incomeAttributesState, lookupState.success, subsidyData]);

  return (
    <div className='full-width'>
      <div className='content-container'>
        <ErrorMessages errors={errors} />
        <p className='strong'>{t('revenues.caption', { ns: 'pie' })}</p>
        <div className="accordion-root">
          <div className={accordionDisplay(isInternal, "accordion")}>
            <ControlledAccordion providerValue={providerValue}>
              <AccordionItem
                header={t("basicProvincialSubsidy", { ns: 'navigation' })}
                key={"basicProvincialSubsidy"}
                itemKey={"basicProvincialSubsidy"}
              >
                <SubsidyProfileTab
                  keys={subsidyKeys}
                  data={subsidyData as Record<string, string | number | null>}
                  handleCommentChange={handleCommentChange}
                  handleCurrencyChange={handleCurrencyChange}
                  valueChangeHandler={handleChange}
                />
              </AccordionItem>
              <AccordionItem
                header={t("operatingRevenue", { ns: 'navigation' })}
                key={"revenue"}
                itemKey={"revenue"}
              >
                <RevenueTab
                  handleTotalIncome={handleIncomeAttrChange}
                  handleCommentChange={handleIncomeAttrCommentChange}
                  name={"revenue"}
                  lookups={lookupState.lookups[1]["revenue"] as IncomeAttributeLookupObject[]}
                  incomeAttributes={
                    (incomeAttributesState.data && (incomeAttributesState.data as Record<string, unknown>[]).length > 0)
                      ? (incomeAttributesState.data as Record<string, unknown>[])
                      : undefined
                  }
                  lookupType={lookupType}
                />
              </AccordionItem>
              <AccordionItem
                header={t("nonOperatingIncomeRevenues", { ns: 'navigation' })}
                key={"nonOperatingIncomeRevenues"}
                itemKey={"nonOperatingIncomeRevenues"}
              >
                <RevenueTab
                  handleTotalIncome={handleIncomeAttrChange}
                  handleCommentChange={handleIncomeAttrCommentChange}
                  name={"nonOperatingIncomeRevenues"}
                  lookups={lookupState.lookups[1]["nonOperatingIncomeRevenues"] as IncomeAttributeLookupObject[]}
                  incomeAttributes={
                    (incomeAttributesState.data && (incomeAttributesState.data as Record<string, unknown>[]).length > 0)
                      ? (incomeAttributesState.data as Record<string, unknown>[])
                      : undefined
                  }
                  lookupType={lookupType}
                />
              </AccordionItem>

              <AccordionItem
                header={t("rentalLoss", { ns: 'navigation' })}
                key={"rentalLoss"}
                itemKey={"rentalLoss"}
              >
                <RevenueTab
                  handleTotalIncome={handleIncomeAttrChange}
                  handleTotalLoss={handleTotalLoss}
                  handleCommentChange={handleIncomeAttrCommentChange}
                  name={"rentalLoss"}
                  lookups={lookupState.lookups[1]["rentalLoss"] as IncomeAttributeLookupObject[]}
                  incomeAttributes={
                    (incomeAttributesState.data && (incomeAttributesState.data as Record<string, unknown>[]).length > 0)
                      ? (incomeAttributesState.data as Record<string, unknown>[])
                      : undefined
                  }
                  lookupType={lookupType}
                />
              </AccordionItem>
            </ControlledAccordion>
          </div>
          {shouldDisplayPreviousYearAttributes() ? <PreviousCampaignIncomeAttributes tabComponent='subsidy' lookupType={lookupType} keys={subsidyKeys} year={String(currentCampaignYear - 1)} /> : <></>}
          {isInternal && shouldDisplayPreviousTwoYearAttributes() ? <PreviousCampaignIncomeAttributes tabComponent='subsidy' lookupType={lookupType} keys={subsidyKeys} year={String(currentCampaignYear - 2)} /> : <></>}
        </div>
        <div className='total-container'>
          <div className="accordion-root">
            <div className={accordionDisplay(isInternal, "accordion")}>
              <Total
                text={t('headers.totalBasicProvincialSubsidy')}
                value={getTotalSubsidy(String(currentCampaignYear), false)}
                className="secondary"
              />
              <Total
                text={t('headers.totalRevenue')}
                value={totalIncome}
              />
              <Total
                text={t('revenues.totalRevenueLoss', { ns: 'pie' })}
                value={totalLoss}
                className='secondary'
                space={false}
                id={'totalLoss'}
              />
            </div>
            {shouldDisplayPreviousYearAttributes() ? <div className={accordionDisplay(isInternal, "right")}>
              <Total
                text={t('headers.totalBasicProvincialSubsidy')}
                value={getTotalSubsidy(String(currentCampaignYear - 1), true)}
                className="secondary"
              />
              <Total
                text={t('headers.totalRevenue')}
                value={calculateTotal(String(currentCampaignYear - 1), true)}
              />
              <Total
                text={t('revenues.totalRevenueLoss', { ns: 'pie' })}
                value={calculateTotalLoss(String(currentCampaignYear - 1), true)}
                className='secondary'
                space={false}
                id={'totalLoss'}
              />
            </div> : <></>}
            {isInternal && shouldDisplayPreviousTwoYearAttributes() ? <div className={accordionDisplay(isInternal, "right")}>
              <Total
                text={t('headers.totalBasicProvincialSubsidy')}
                value={getTotalSubsidy(String(currentCampaignYear - 2), true)}
                className="secondary"
              />
              <Total
                text={t('headers.totalRevenue')}
                value={calculateTotal(String(currentCampaignYear - 2), true)}
              />
              <Total
                text={t('revenues.totalRevenueLoss', { ns: 'pie' })}
                value={calculateTotalLoss(String(currentCampaignYear - 2), true)}
                className='secondary'
                space={false}
                id={'totalLoss'}
              />
            </div> : <></>}
          </div>
        </div>
      </div>
    </div>
  )
}